import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "@/i18n/useTranslation";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format, addDays } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { parsePlainDateString, toPlainDateString } from "@/utils/dateTransforms";

interface CreateSprintDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateSprint: (title: string, startDate: Date, endDate: Date, status: string, boardId: string | null) => void;
  boardId?: string | null;
}

interface BoardInfo {
  id: string;
  project_id: number;
}

interface ProjectInfo {
  id: number;
  name: string;
}

interface Sprint {
  id: string;
  title: string;
  start_date: string;
  end_date: string;
  status: 'planned' | 'in_progress' | 'completed' | 'backlog';
  board_id: string;
}

export function CreateSprintDialog({ isOpen, onClose, onCreateSprint, boardId }: CreateSprintDialogProps) {
  const { t, language } = useTranslation();
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState<Date | undefined>(new Date());
  const [endDate, setEndDate] = useState<Date | undefined>(
    new Date(new Date().setDate(new Date().getDate() + 14)) // Default to a 2-week sprint
  );
  // Sprint status will always be 'planned' but we don't show it in the UI
  const status = 'planned';
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);
  const [boards, setBoards] = useState<BoardInfo[]>([]);
  const [projects, setProjects] = useState<ProjectInfo[]>([]);
  
  const dateLocale = language === 'es' ? es : enUS;
  
  // Set default dates based on the last sprint's end date
  const setDefaultDates = useCallback((targetBoardId: string) => {
    // Fetch sprints for the selected board to find the latest one
    const fetchSprintsAndSetDates = async () => {
      try {
        const { data, error } = await supabase
          .from('sprints')
          .select('*')
          .eq('board_id', targetBoardId)
          .neq('status', 'backlog') // Exclude backlog sprints
          .order('end_date', { ascending: false }); // Get the latest sprint first
        
        if (error) {
          console.error('Error fetching sprints for dates:', error);
          return;
        }
        
        if (data && data.length > 0) {
          // Get the latest sprint's end date
          const latestSprint = data[0];
          
          // Set start date to the day after the last sprint ends
          const lastSprintEndDate = parsePlainDateString(latestSprint.end_date);
          if (lastSprintEndDate) {
            const newStartDate = addDays(lastSprintEndDate, 1);
            const newEndDate = addDays(newStartDate, 14);
            
            setStartDate(newStartDate);
            setEndDate(newEndDate);
          }
        } else {
          // No sprints exist yet, use today as the start date
          const today = new Date();
          setStartDate(today);
          setEndDate(addDays(today, 14));
        }
      } catch (err) {
        console.error('Error setting default sprint dates:', err);
      }
    };
    
    fetchSprintsAndSetDates();
  }, []);
  
  // Fetch available boards and their associated project names
  useEffect(() => {
    const fetchBoards = async () => {
      try {
        const { data: userProfile } = await supabase.auth.getSession();
        if (userProfile?.session?.user) {
          const { data: userData } = await supabase
            .from('users')
            .select('company_id')
            .eq('user_id', userProfile.session.user.id)
            .single();
            
          if (userData?.company_id) {
            // Get all projects for the company
            const { data: clients } = await supabase
              .from('clients')
              .select('id')
              .eq('company_id', userData.company_id);
              
            if (clients && clients.length > 0) {
              const clientIds = clients.map(client => client.id);
              
              // Get projects for these clients
              const { data: projectsData } = await supabase
                .from('projects')
                .select('id, name')
                .in('client_id', clientIds);
                
              if (projectsData && projectsData.length > 0) {
                setProjects(projectsData);
                
                const projectIds = projectsData.map(project => project.id);
                
                // Get boards for these projects
                const { data: boardsData, error } = await supabase
                  .from('boards')
                  .select('id, project_id')
                  .in('project_id', projectIds);
                  
                if (error) {
                  console.error('Error fetching boards:', error);
                  return;
                }
                
                if (boardsData) {
                  setBoards(boardsData);
                  
                  // Set default board if one is provided
                  if (boardId) {
                    setSelectedBoardId(boardId);
                    setDefaultDates(boardId);
                  } else if (boardsData.length > 0) {
                    setSelectedBoardId(boardsData[0].id);
                    setDefaultDates(boardsData[0].id);
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching boards:', error);
      }
    };
    
    if (isOpen) {
      fetchBoards();
      
      // If a specific board ID was provided, use it
      if (boardId) {
        setSelectedBoardId(boardId);
      }
    }
  }, [isOpen, boardId, setDefaultDates]);
  
  // Update default dates when the selected board changes
  useEffect(() => {
    if (selectedBoardId) {
      setDefaultDates(selectedBoardId);
    }
  }, [selectedBoardId, setDefaultDates]);

  const resetForm = () => {
    setTitle('');
    // Let the useEffect handle resetting the dates based on the board's latest sprint
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !startDate || !endDate || !selectedBoardId) return;
    
    onCreateSprint(title, startDate, endDate, status, selectedBoardId);
    resetForm();
    onClose();
  };

  // Get project name for a board
  const getProjectNameForBoard = (boardId: string): string => {
    const board = boards.find(b => b.id === boardId);
    if (!board) return t('board.boardTitle');
    
    const project = projects.find(p => p.id === board.project_id);
    return project ? project.name : t('board.boardTitle');
  };

  // When the user selects a start date, update the end date to be 2 weeks after
  const handleStartDateChange = (date: Date | undefined) => {
    setStartDate(date);
    if (date) {
      setEndDate(addDays(date, 14));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('board.createSprint')}</DialogTitle>
          <DialogDescription>
            {t('board.sprintDescription')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="title" className="text-sm font-medium">
              {t('board.sprintTitle')}
            </label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('board.sprintTitle')}
              required
              autoFocus
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="startDate" className="text-sm font-medium">
                {t('board.startDate')}
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="startDate"
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? (
                      format(startDate, 'PPP', { locale: dateLocale })
                    ) : (
                      <span>{t('board.pickDate')}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={handleStartDateChange}
                    locale={dateLocale}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="space-y-2">
              <label htmlFor="endDate" className="text-sm font-medium">
                {t('board.endDate')}
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="endDate"
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? (
                      format(endDate, 'PPP', { locale: dateLocale })
                    ) : (
                      <span>{t('board.pickDate')}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    locale={dateLocale}
                    fromDate={startDate} // Can't select a date before the start date
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          
          {!boardId && boards.length > 0 && (
            <div className="space-y-2">
              <label htmlFor="board" className="text-sm font-medium">
                {t('board.boardTitle')}
              </label>
              <Select value={selectedBoardId || ''} onValueChange={(value) => setSelectedBoardId(value)}>
                <SelectTrigger id="board">
                  <SelectValue placeholder={t('board.selectBoard')} />
                </SelectTrigger>
                <SelectContent>
                  {boards.map((board) => (
                    <SelectItem key={board.id} value={board.id}>
                      {getProjectNameForBoard(board.id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <DialogFooter className="gap-2 flex-wrap">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit">
              {t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
